'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import styles from './estimation.module.scss';
import { Button, Container, Input, Notification, Space, Textarea } from '@mantine/core';
import { DropzoneCloudinary } from '@/src/components/EditorText/Dropzone';
import FullScreenImageHalf from '@/src/components/SliceMachine/FullScreenImageHalf/FullScreenImageHalf';
import { Title } from '@/src/components/Title/Title';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';

export default function Estimation() {
  const dictionary = useDictionary();
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [comment, setComment] = useState<string>('');
  const [notification, setNotification] = useState<{
    message: string;
    color: 'red' | 'green';
  } | null>(null);
  const router = useRouter();

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!name || !phone || !mainImage) {
      setNotification({
        message: 'Please fill in all required fields before submitting.',
        color: 'red',
      });
      setLoading(false);
      return;
    }

    const estimationData = { name, phone, mainImage, comment };

    try {
      const response = await fetch('/api/estimation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(estimationData),
      });

      setLoading(false);

      if (response.ok) {
        setNotification({
          message: 'Your estimation request has been sent successfully!',
          color: 'green',
        });

        // Redirect after success
        setTimeout(() => {
          router.push('/articles');
        }, 2000);
      } else {
        setNotification({
          message:
            response.status === 400
              ? 'The request was not processed. Have you filled all the fields correctly?'
              : 'An error occurred. Please try again.',
          color: 'red',
        });
      }
    } catch (error) {
      setLoading(false);
      setNotification({
        message: 'An error occurred while submitting your request. Please try again later.',
        color: 'red',
      });
      console.error('Error submitting the estimation:', error);
    }
  };

  return (
    <>
      {/* @ts-ignore */}
      <FullScreenImageHalf data={dictionary.pages.estimation.slices[0]} />

      <Container>
        <Container style={{ textAlign: 'center' }}>
          <Title title={dictionary.pages.estimation.cta} />
        </Container>

        {/* Display Notification */}
        {notification && (
          <Notification
            color={notification.color}
            onClose={() => setNotification(null)}
            className="mb-4"
          >
            {notification.message}
          </Notification>
        )}

        <DropzoneCloudinary setImageUrl={setMainImage} imageUrl={mainImage} />
        <Space h="lg" />
        <Space h="lg" />
        <Space h="lg" />
        <form
          onSubmit={handleSubmit}
          className="flex flex-col space-y-4"
          style={{ textAlign: 'center' }}
        >
          <Input
            type="text"
            placeholder={dictionary.pages.estimations.name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Space h="lg" />
          <Input
            type="tel"
            placeholder={dictionary.pages.estimations.phone}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
          <Space h="lg" />
          <Textarea
            placeholder={dictionary.pages.estimations.comment}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <Space h="lg" />
          <Space h="lg" />
          <Space h="lg" />
          <Space h="lg" />
          <Button type="submit" disabled={loading} miw="250px" className={styles.button}>
            {loading ? dictionary.pages.estimations.sending : dictionary.pages.estimations.estimate}
          </Button>
        </form>
      </Container>
    </>
  );
}
